import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usersApi } from '@/lib/users/users.api';
import type { User } from '@/lib/users/user.types';
import { Modal } from '@symphenia/ui-components';

interface Props {
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangeEmailDialog({ user, onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await usersApi.changeEmail(user.id, email.trim());
      onSuccess();
      onClose();
      setEmail('');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('common.error');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-md" hideCloseButton>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('users.changeEmail')}</h2>
        <button
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('users.currentEmail')}
          </label>
          <p className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {user.email}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('users.newEmail')} *
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="name@firma.at"
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('users.changeEmailHint')}</p>
        </div>

        {error && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {/* AP-36 5.1: eine Handlung trägt ihr Verb, nicht „Speichern". */}
            {loading ? t('common.loading') : t('users.changeEmail')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
